import { Capacitor } from "@capacitor/core";
import {
  getKeycloak,
  initializeKeycloak as webInitializeKeycloak,
} from "@shared/security/keycloak";
import {
  nativeLogin,
  nativeLogout,
  nativeDirectLogin,
  getStoredRefreshToken,
  storeRefreshToken,
  clearStoredTokens,
} from "./nativeAuth";

export { getKeycloak };

const isNative = Capacitor.isNativePlatform();
const FORCE_TOKEN_REFRESH = -1;

async function applyTokens(accessToken: string, refreshToken: string): Promise<void> {
  const keycloak = getKeycloak();
  keycloak.token = accessToken;
  keycloak.refreshToken = refreshToken;
  await keycloak.updateToken(FORCE_TOKEN_REFRESH);
  if (keycloak.refreshToken) {
    await storeRefreshToken(keycloak.refreshToken);
  }
}

async function nativeInitialize(): Promise<boolean> {
  const keycloak = getKeycloak();

  await keycloak.init({ checkLoginIframe: false });

  const stored = await getStoredRefreshToken();
  if (stored) {
    keycloak.refreshToken = stored;
    try {
      await keycloak.updateToken(FORCE_TOKEN_REFRESH);
      if (keycloak.token) {
        if (keycloak.refreshToken) await storeRefreshToken(keycloak.refreshToken);
        return true;
      }
    } catch {
      await clearStoredTokens();
    }
  }

  // Login se ne forsira ovdje — main.ts otvara login i nudi retry ako korisnik prekine.
  return false;
}

export function initializeKeycloak(): Promise<boolean> {
  return isNative ? nativeInitialize() : webInitializeKeycloak();
}

export async function login(): Promise<void> {
  if (!isNative) {
    await getKeycloak().login();
    return;
  }
  const tokens = await nativeLogin();
  await applyTokens(tokens.access_token, tokens.refresh_token);
}

// Direct Access Grant login for the native login screen — bypasses the system browser entirely.
export async function loginWithCredentials(username: string, password: string): Promise<void> {
  const tokens = await nativeDirectLogin(username, password);
  await applyTokens(tokens.access_token, tokens.refresh_token);
}

export async function logout(options?: { redirectUri?: string }): Promise<void> {
  const keycloak = getKeycloak();
  if (!isNative) {
    await keycloak.logout(options);
    return;
  }
  const { unregisterPush } = await import("@/services/push/PushService");
  await unregisterPush();
  await nativeLogout();
  keycloak.authenticated = false;
  keycloak.token = undefined;
  keycloak.refreshToken = undefined;
  keycloak.tokenParsed = undefined;
  window.location.reload();
}
